import type {
  AddOrganizationFilesParams,
  HydratedOrganizationFile,
} from "@forgepoint/shared";
import { captureException } from "@/lib/analytics";

/** A successfully uploaded file awaiting Vault registration. */
export interface VaultUploadEntry {
  assetId: string;
  relativePath: string;
}

/** Registration counts, reported after each settled batch and by `finish`. */
export interface VaultRegistrationProgress {
  registeredCount: number;
  skippedExistingCount: number;
  failedCount: number;
}

/**
 * Dependencies for the batcher: the destination folder, the files store's
 * `addFiles`, and a callback awaited after each settled batch.
 */
export interface VaultRegistrationBatcherOptions {
  parentFolderId: string | null;
  addFiles: (
    params: AddOrganizationFilesParams,
  ) => Promise<HydratedOrganizationFile[] | null>;
  deleteAsset?: (assetId: string) => Promise<boolean>;
  onBatchSettled?: (
    progress: VaultRegistrationProgress,
  ) => void | Promise<void>;
}

// 200 entries of {assetId, relativePath} stay far below the 1MiB body limit.
const MAX_BATCH_ENTRIES = 200;

/**
 * Registers uploaded Vault files during the upload, in bounded idempotent
 * (`skipExistingPaths`) batches. Each batch is sent once; a failed batch
 * counts its files as failed, and re-running the same upload is safe.
 */
export class VaultRegistrationBatcher {
  private pendingEntries: VaultUploadEntry[] = [];
  private readonly totals: VaultRegistrationProgress = {
    registeredCount: 0,
    skippedExistingCount: 0,
    failedCount: 0,
  };

  constructor(private readonly options: VaultRegistrationBatcherOptions) {}

  /** Buffers one uploaded file; sends a batch when the buffer is full. */
  async add(entry: VaultUploadEntry): Promise<void> {
    this.pendingEntries.push(entry);
    if (this.pendingEntries.length >= MAX_BATCH_ENTRIES) {
      await this.flush();
    }
  }

  /** Sends the remaining entries and returns the final counts. */
  async finish(): Promise<VaultRegistrationProgress> {
    await this.flush();
    return { ...this.totals };
  }

  private async flush(): Promise<void> {
    if (this.pendingEntries.length === 0) {
      return;
    }
    const batch = this.pendingEntries;
    this.pendingEntries = [];

    const registered = await this.options.addFiles({
      parentFolderId: this.options.parentFolderId,
      files: batch,
      skipExistingPaths: true,
    });
    if (registered) {
      this.totals.registeredCount += registered.length;
      this.totals.skippedExistingCount += batch.length - registered.length;
      await this.deleteSkippedAssets(batch, registered);
    } else {
      this.totals.failedCount += batch.length;
    }
    await this.options.onBatchSettled?.({ ...this.totals });
  }

  /**
   * Deletes the assets behind files the vault skipped, so an upload the
   * pre-flight did not catch leaves no orphan. Deletes run one at a time: a
   * whole skipped batch would otherwise burst hundreds of requests at once.
   */
  private async deleteSkippedAssets(
    batch: VaultUploadEntry[],
    registered: HydratedOrganizationFile[],
  ): Promise<void> {
    const deleteAsset = this.options.deleteAsset;
    if (!deleteAsset || registered.length === batch.length) {
      return;
    }

    const registeredAssetIds = new Set(registered.map((file) => file.assetId));
    const skipped = batch.filter(
      (entry) => !registeredAssetIds.has(entry.assetId),
    );
    for (const entry of skipped) {
      await deleteAsset(entry.assetId).catch((error) => {
        captureException(error as Error);
        return false;
      });
    }
  }
}
